import { Injectable } from '@angular/core';
import { CapitalizeFirstPipe } from '@core/pipes/capitalize-first.pipe';
import { MeetingTimelineStep } from '@meeting/components/config/meeting-timeline-steps';
import {
  EMeetingStatuses,
  EParticipantRoles,
  IMeeting,
  IParticipantResult,
} from '@meeting/models/meeting.model';
import { TranslocoService } from '@ngneat/transloco';
import { DatesService } from '@shared/services/dates.service';

@Injectable({
  providedIn: 'root',
})
export class MeetingTimelineService {
  constructor(
    private transloco: TranslocoService,
    private _capitalizePipe: CapitalizeFirstPipe,
    private _dateService: DatesService,
  ) {}

  computeStepExtras(
    step: MeetingTimelineStep,
    status: EMeetingStatuses,
    meeting: IMeeting,
    viewRole: EParticipantRoles, // Remplace currentUserId
    stepType: 'prepare' | 'conduct' | 'sign',
  ): void {
    step.tooltip = '';

    // Need to reset those value
    step.canViewMyAnswers = false;
    step.canViewRevieweeAnswers = false;
    step.canViewAllAnswers = false;

    step.scheduleConductDate = undefined;

    if (stepType === 'prepare' && viewRole === EParticipantRoles.REVIEWER) {
      switch (meeting.status) {
        case EMeetingStatuses.PREPARED_BY_REVIEWEE:
          step.canViewMyAnswers = false;
          step.canViewRevieweeAnswers =
            !!meeting.config?.canViewRevieweeAnswers;
          step.canViewAllAnswers = false;
          break;
        case EMeetingStatuses.PREPARED_BY_REVIEWER:
          step.canViewMyAnswers = !!meeting.config?.canViewRevieweeAnswers;
          step.canViewRevieweeAnswers = false;
          step.canViewAllAnswers = false;
          break;
        case EMeetingStatuses.PREPARED_BY_BOTH:
          step.canViewMyAnswers = false;
          step.canViewRevieweeAnswers = false;
          step.canViewAllAnswers = true;
          break;
        default:
          step.canViewMyAnswers = false;
          step.canViewRevieweeAnswers = false;
          step.canViewAllAnswers = false;
          break;
      }
    }

    const reviewer = this.getParticipantByRole(
      meeting,
      EParticipantRoles.REVIEWER,
    );
    const reviewee = this.getParticipantByRole(
      meeting,
      EParticipantRoles.REVIEWEE,
    );

    if (
      ['conduct'].includes(stepType) &&
      [
        EMeetingStatuses.CREATED,
        EMeetingStatuses.PREPARED_BY_REVIEWER,
        EMeetingStatuses.PREPARED_BY_REVIEWEE,
        EMeetingStatuses.PREPARED_BY_BOTH,
        EMeetingStatuses.STARTED,
      ].includes(meeting?.status) &&
      (meeting?.scheduledConductStartDate || meeting?.effectiveConductDate)
    ) {
      step.textKey = 'MEETING.TIMELINE.CONDUCT_DATE_PLANNED';
      step.scheduleConductDate = {
        date: this._dateService.formatDate(
          meeting.effectiveConductDate ?? meeting.scheduledConductStartDate,
          'P',
        ),
        time: meeting.effectiveConductDate
          ? undefined
          : this._dateService.formatDate(
              meeting.scheduledConductStartDate,
              'p',
            ),
      };
    }

    if (
      ['conduct'].includes(stepType) &&
      [
        EMeetingStatuses.CLOSED_BY_BOTH,
        EMeetingStatuses.FINISHED_BY_REVIEWEE,
        EMeetingStatuses.FINISHED_BY_REVIEWER,
        EMeetingStatuses.READY_TO_SIGN,
      ].includes(meeting?.status) &&
      (meeting?.scheduledConductEndDate || meeting?.effectiveConductDate)
    ) {
      step.textKey = 'MEETING.TIMELINE.CONDUCT_DATE_EFFECTIVE';
      step.scheduleConductDate = {
        date: this._dateService.formatDate(
          meeting.effectiveConductDate ?? meeting.scheduledConductEndDate,
          'P',
        ),
        time: meeting.effectiveConductDate
          ? undefined
          : this._dateService.formatDate(meeting.scheduledConductEndDate, 'p'),
      };
    }

    if (
      ['conduct'].includes(stepType) &&
      !(
        meeting?.scheduledConductStartDate ||
        meeting?.scheduledConductEndDate ||
        meeting?.effectiveConductDate
      )
    ) {
      step.textKey = 'MEETING.TIMELINE.CONDUCT';
    }

    step.tooltip = this.getTooltip(
      status,
      stepType,
      reviewer,
      reviewee,
      viewRole,
    );

    if (stepType !== 'conduct') {
      step.textExtra = this.getTextExtra(status, stepType);
    }
  }

  private getTextExtra(
    status: EMeetingStatuses,
    stepType: 'prepare' | 'sign',
  ): string {
    const statusesForOneOfTwo =
      stepType === 'prepare'
        ? [
            EMeetingStatuses.PREPARED_BY_REVIEWEE,
            EMeetingStatuses.PREPARED_BY_REVIEWER,
          ]
        : [
            EMeetingStatuses.FINISHED_BY_REVIEWEE,
            EMeetingStatuses.FINISHED_BY_REVIEWER,
          ];

    if (
      (status === EMeetingStatuses.STARTED && stepType === 'prepare') ||
      (status === EMeetingStatuses.READY_TO_SIGN && stepType === 'sign')
    ) {
      return '(0/2)';
    }

    if (statusesForOneOfTwo.includes(status)) {
      return '(1/2)';
    }

    return '';
  }

  private getTooltip(
    status: EMeetingStatuses,
    stepType: 'prepare' | 'conduct' | 'sign',
    reviewer?: IParticipantResult,
    reviewee?: IParticipantResult,
    viewRole?: EParticipantRoles,
  ): string {
    if (
      (status === EMeetingStatuses.STARTED && stepType === 'prepare') ||
      (status === EMeetingStatuses.READY_TO_SIGN && stepType === 'sign')
    ) {
      return this.transloco.translate('MEETING.TIMELINE.TOOLTIP_WAITING_BOTH');
    }

    if (
      (status === EMeetingStatuses.PREPARED_BY_REVIEWEE &&
        stepType === 'prepare') ||
      (status === EMeetingStatuses.FINISHED_BY_REVIEWEE && stepType === 'sign')
    ) {
      return this.getTooltipForRevieweePreparation(reviewer, viewRole);
    }

    if (
      (status === EMeetingStatuses.PREPARED_BY_REVIEWER &&
        stepType === 'prepare') ||
      (status === EMeetingStatuses.FINISHED_BY_REVIEWER && stepType === 'sign')
    ) {
      return this.getTooltipForReviewerPreparation(reviewee, viewRole);
    }

    return '';
  }

  private getTooltipForRevieweePreparation(
    reviewer?: IParticipantResult,
    viewRole?: EParticipantRoles,
  ): string {
    if (viewRole === EParticipantRoles.ALL) {
      return this.transloco.translate(
        'MEETING.TIMELINE.TOOLTIP_WAITING_REVIEWER',
        {
          username: `${this._capitalizePipe.transform(reviewer.firstName)} ${reviewer.lastName}`,
        },
      );
    }

    if (viewRole === EParticipantRoles.REVIEWEE) {
      return this.transloco.translate(
        'MEETING.TIMELINE.TOOLTIP_WAITING_REVIEWER',
        {
          username: `${this._capitalizePipe.transform(reviewer.firstName)} ${reviewer.lastName}`,
        },
      );
    }

    if (viewRole === EParticipantRoles.REVIEWER) {
      return this.transloco.translate(
        'MEETING.TIMELINE.TOOLTIP_WAITING_FOR_YOU',
      );
    }

    return '';
  }

  private getTooltipForReviewerPreparation(
    reviewee?: IParticipantResult,
    viewRole?: EParticipantRoles,
  ): string {
    if (viewRole === EParticipantRoles.ALL) {
      return this.transloco.translate(
        'MEETING.TIMELINE.TOOLTIP_WAITING_REVIEWEE',
        {
          username: `${this._capitalizePipe.transform(reviewee.firstName)} ${reviewee.lastName}`,
        },
      );
    }

    if (viewRole === EParticipantRoles.REVIEWER) {
      return this.transloco.translate(
        'MEETING.TIMELINE.TOOLTIP_WAITING_REVIEWEE',
        {
          username: `${this._capitalizePipe.transform(reviewee.firstName)} ${reviewee.lastName}`,
        },
      );
    }

    if (viewRole === EParticipantRoles.REVIEWEE) {
      return this.transloco.translate(
        'MEETING.TIMELINE.TOOLTIP_WAITING_FOR_YOU',
      );
    }

    return '';
  }

  private getParticipantByRole(
    meeting: IMeeting,
    role: EParticipantRoles,
  ): IParticipantResult | undefined {
    return meeting.participants.find((user) => user.role === role);
  }
}
